import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  EmailAuthProvider,
  getAuth,
  getMultiFactorResolver,
  multiFactor,
  MultiFactorResolver,
  reauthenticateWithCredential,
  reauthenticateWithPopup,
  TotpMultiFactorGenerator,
  TotpSecret
} from 'firebase/auth';
import QRCode from 'qrcode';

import { MfaQrStep, MfaReAuthForm } from '@/components/common';
import { Button, Label, LABEL_TYPE, ToggleSwitch } from '@/components/generic';
import { ButtonVariant } from '@/components/generic/Button/button-config';
import {
  createAuthProvider,
  getSsoProviderByFirebaseId,
  isSsoAuthUser
} from '@/configuration/ssoProviders';
import { queryClient } from '@/configuration/react-query';
import { useAuth } from '@/contexts';
import { showToast, ToastType } from '@/helpers/toast';
import { useGetCurrentUserQuery, useUpdateMfaStatusMutation } from '@/services';
import { TestIds } from '@/shared/test-ids';
import { SsoProviderConfig } from '@/types/ssoProvider';

type MfaSetupStep = 'idle' | 'qr' | 'verify';

export const MfaSettings = () => {
  const [t] = useTranslation('common');
  const { signInProvider } = useAuth();
  const { data: user, refetch } = useGetCurrentUserQuery(true);
  const isMfaEnabled = user?.data?.isMfaEnabled ?? false;
  const ssoProviderConfig: SsoProviderConfig | undefined =
    getSsoProviderByFirebaseId(signInProvider);
  const canReAuthWithSso = isSsoAuthUser(getAuth().currentUser) && !!ssoProviderConfig;

  const [step, setStep] = useState<MfaSetupStep>('idle');
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState<string>('');
  const [totpSecret, setTotpSecret] = useState<TotpSecret | null>(null);
  const [totpCode, setTotpCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [reAuthPassword, setReAuthPassword] = useState('');
  const [showReAuthPassword, setShowReAuthPassword] = useState(false);
  const [needsReAuth, setNeedsReAuth] = useState(false);
  const [pendingAction, setPendingAction] = useState<'enroll' | 'disable' | null>(null);
  const [reAuthStep, setReAuthStep] = useState<'password' | 'totp'>('password');
  const [mfaResolver, setMfaResolver] = useState<MultiFactorResolver | null>(null);
  const [reAuthTotpCode, setReAuthTotpCode] = useState('');

  const { mutate: updateMfaStatus } = useUpdateMfaStatusMutation(queryClient, {
    onSuccess: () => refetch(),
    onError: () => showToast(ToastType.ERROR, t('mfaUpdateError'))
  });

  const startEnrollment = async () => {
    const auth = getAuth();
    const currentUser = auth.currentUser;
    if (!currentUser) {
      return;
    }

    const session = await multiFactor(currentUser).getSession();
    const secret = await TotpMultiFactorGenerator.generateSecret(session);
    setTotpSecret(secret);

    const otpAuthUrl = secret.generateQrCodeUrl(currentUser.email ?? '', 'Sentoa');
    const dataUrl = await QRCode.toDataURL(otpAuthUrl);
    setQrCodeDataUrl(dataUrl);
    setStep('qr');
  };

  const continuePendingAction = async () => {
    if (pendingAction === 'enroll') {
      await startEnrollment();
    } else if (pendingAction === 'disable') {
      await disableMfa();
    }
    setPendingAction(null);
  };

  const handleStartEnrollment = async () => {
    setIsLoading(true);
    try {
      await startEnrollment();
    } catch (err: unknown) {
      const code = (err as { code?: string })?.code;
      if (code === 'auth/requires-recent-login' || code === 'auth/user-token-expired') {
        setNeedsReAuth(true);
        setPendingAction('enroll');
      } else {
        showToast(ToastType.ERROR, t('mfaSetupError'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleReAuth = async () => {
    setIsLoading(true);
    try {
      const auth = getAuth();
      const currentUser = auth.currentUser;
      if (!currentUser?.email) {
        return;
      }

      const credential = EmailAuthProvider.credential(currentUser.email, reAuthPassword);
      await reauthenticateWithCredential(currentUser, credential);
      setNeedsReAuth(false);
      setReAuthPassword('');
      await continuePendingAction();
    } catch (err: unknown) {
      const code = (err as { code?: string })?.code;
      if (code === 'auth/multi-factor-auth-required') {
        const auth = getAuth();
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const resolver = getMultiFactorResolver(auth, err as any);
        setMfaResolver(resolver);
        setReAuthStep('totp');
      } else {
        showToast(ToastType.ERROR, t('mfaReAuthError'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSsoReAuth = async () => {
    setIsLoading(true);
    try {
      const currentUser = getAuth().currentUser;
      if (!currentUser || !ssoProviderConfig) {
        return;
      }

      await reauthenticateWithPopup(currentUser, createAuthProvider(ssoProviderConfig));
      setNeedsReAuth(false);
      setReAuthPassword('');
      await continuePendingAction();
    } catch (err: unknown) {
      const code = (err as { code?: string })?.code;
      if (code !== 'auth/popup-closed-by-user' && code !== 'auth/cancelled-popup-request') {
        showToast(ToastType.ERROR, t('mfaReAuthError'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleReAuthTotp = async () => {
    if (!mfaResolver) {
      return;
    }
    setIsLoading(true);
    try {
      const totpFactor = mfaResolver.hints.find(
        (h) => h.factorId === TotpMultiFactorGenerator.FACTOR_ID
      );
      if (!totpFactor) {
        throw new Error('No TOTP factor');
      }

      const assertion = TotpMultiFactorGenerator.assertionForSignIn(totpFactor.uid, reAuthTotpCode);
      await mfaResolver.resolveSignIn(assertion);

      setNeedsReAuth(false);
      setReAuthPassword('');
      setReAuthTotpCode('');
      setReAuthStep('password');
      setMfaResolver(null);

      if (pendingAction === 'disable') {
        await disableMfa();
      }
      setPendingAction(null);
    } catch {
      showToast(ToastType.ERROR, t('mfaInvalidCode'));
      setReAuthTotpCode('');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyAndEnroll = async () => {
    if (!totpSecret) {
      return;
    }
    setIsLoading(true);
    try {
      const auth = getAuth();
      const currentUser = auth.currentUser;
      if (!currentUser) {
        return;
      }

      const assertion = TotpMultiFactorGenerator.assertionForEnrollment(totpSecret, totpCode);
      await multiFactor(currentUser).enroll(assertion, 'TOTP');

      updateMfaStatus({ isMfaEnabled: true });
      showToast(ToastType.SUCCESS, t('mfaEnrolledSuccess'));
      setStep('idle');
      setTotpCode('');
    } catch {
      showToast(ToastType.ERROR, t('mfaInvalidCode'));
      setTotpCode('');
    } finally {
      setIsLoading(false);
    }
  };

  const disableMfa = async () => {
    const auth = getAuth();
    const currentUser = auth.currentUser;
    if (!currentUser) {
      return;
    }

    const mfaFactors = multiFactor(currentUser).enrolledFactors;
    for (const factor of mfaFactors) {
      await multiFactor(currentUser).unenroll(factor);
    }

    updateMfaStatus({ isMfaEnabled: false });
    showToast(ToastType.SUCCESS, t('mfaDisabledSuccess'));
  };

  const handleDisableMfa = async () => {
    setIsLoading(true);
    try {
      await disableMfa();
    } catch (err: unknown) {
      const code = (err as { code?: string })?.code;
      if (code === 'auth/requires-recent-login' || code === 'auth/user-token-expired') {
        setNeedsReAuth(true);
        setPendingAction('disable');
      } else {
        showToast(ToastType.ERROR, t('mfaDisableError'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelReAuth = () => {
    setNeedsReAuth(false);
    setReAuthPassword('');
    setReAuthStep('password');
    setReAuthTotpCode('');
    setMfaResolver(null);
    setPendingAction(null);
  };

  return (
    <div className='flex flex-col gap-4'>
      <Label text={t('mfaTitle')} type={LABEL_TYPE.H3_BOLD} className='!text-lg' />

      {needsReAuth && (
        <div className='flex flex-col gap-3'>
          <MfaReAuthForm
            reAuthStep={reAuthStep}
            reAuthPassword={reAuthPassword}
            onReAuthPasswordChange={setReAuthPassword}
            showReAuthPassword={showReAuthPassword}
            onToggleShowReAuthPassword={() => setShowReAuthPassword((prev) => !prev)}
            isLoading={isLoading}
            onConfirm={handleReAuth}
            reAuthTotpCode={reAuthTotpCode}
            onReAuthTotpChange={setReAuthTotpCode}
            onVerifyTotp={handleReAuthTotp}
            onCancel={handleCancelReAuth}
          />
          {reAuthStep === 'password' && canReAuthWithSso && (
            <Button
              variant={ButtonVariant.SECONDARY}
              disabled={isLoading}
              handleOnClick={() => {
                void handleSsoReAuth();
              }}
              className='w-fit'>
              {t('confirmWithSsoProvider')}
            </Button>
          )}
        </div>
      )}

      {!needsReAuth && step === 'idle' && (
        <div className='flex items-center gap-3'>
          <ToggleSwitch
            toggled={isMfaEnabled}
            handleToggle={isMfaEnabled ? handleDisableMfa : handleStartEnrollment}
            disabled={isLoading}
            data-testid={TestIds.mfa.settingsToggle}
          />
          <Label
            text={isMfaEnabled ? t('mfaEnabled') : t('mfaDisabled')}
            type={LABEL_TYPE.PARAGRAPH1}
            className={isMfaEnabled ? '!text-green-600' : '!text-secondary-gray'}
          />
        </div>
      )}

      {step === 'qr' && (
        <MfaQrStep
          qrCodeDataUrl={qrCodeDataUrl}
          totpCode={totpCode}
          onTotpChange={setTotpCode}
          isLoading={isLoading}
          onVerify={handleVerifyAndEnroll}
          onSecondaryAction={() => {
            setStep('idle');
            setTotpCode('');
          }}
          secondaryActionLabel={t('cancel')}
        />
      )}
    </div>
  );
};
